import React from 'react'

import { withStyles } from '@material-ui/core/styles'
import { Divider, MenuList } from '@mui/material'
import { connect } from 'react-redux'

import type OLFeature from 'ol/Feature'
import type OLGeomSimpleGeometry from 'ol/geom/SimpleGeometry'
import type OLLayer from 'ol/layer/Layer'

import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import WhatsAroundMenuItem from 'drawer/tabs/info/panels/WhatsAroundMenuItem'
import WhatsHereMenuItem from 'drawer/tabs/info/panels/WhatsHereMenuItem'
import { ShowWhatsHere } from 'drawer/tabs/info/WhatsHereUtils'
import { clearMapContextMenu } from 'map/actions'
import ContextMenu from 'utils/ContextMenu'
import { getFeatureInfoAtPixel, runWhatsHere, runWhatsThis } from 'utils/MapUtils'

interface MapContextMenuProps {
  classes: any;
  contextMenu: any;
  dispatchClearMapContextMenu: () => void;
}

const styles = {
  divider: {
    '&&.MuiDivider-root': {
      marginBottom: 6,
      marginTop: 6
    }
  },
  menuList: {
    '&&.MuiList-root': {
      paddingLeft: 10,
      paddingRight: 10
    }
  }
}

class MapContextMenu_ extends React.PureComponent<MapContextMenuProps> {
  getMenuItems = () => {
    const { classes, contextMenu, dispatchClearMapContextMenu } = this.props

    const { coordinate, pixel } = contextMenu

    // TODO: create proper type interface for featureInfo
    const featureInfos = getFeatureInfoAtPixel(pixel) as unknown as { olFeature: OLFeature; olLayer: OLLayer }[]

    // Use the first feature found under the pixel
    const featureInfo = featureInfos[0]

    const { olFeature, olLayer } = featureInfo || {}

    // console.debug('getMenuItems', showMenu, position, coordinate, olFeature, olLayer)

    // Store the titleid of the layer of the clicked feature for use in map/actions.js
    // This will allow for the layer that the clicked feature belongs to to be shown first
    //
    // TODO: move this outside of this method as it introduces side effects
    const titleid = olLayer ? olLayer.get('titleid') : null
    Model.set('app.map.click.titleid', titleid)

    // Extract geometry type
    const olGeometry = olFeature && olFeature.getGeometry() as OLGeomSimpleGeometry
    const geomTypeOrig = (olGeometry && olGeometry.getType()) || ''

    // Simplify geometry types to either 'line', 'point' or 'polygon'
    const geomTypeSimple = PoziUtils.getGeomTypeSimple(geomTypeOrig)

    // When clicking on a point feature, use the feature coordinates
    // otherwise, use the click coordinates
    let whatsHereCoordinates = coordinate

    // Use the coordinates of the point feature instead of where the user clicked
    if (geomTypeSimple === 'point') {
      const featureCoordinates = olGeometry?.getFirstCoordinate() // this flattens multipoint coordinates
      // console.debug('featureCoordinates', featureCoordinates, olGeometry)

      whatsHereCoordinates = featureCoordinates
    } else if (geomTypeSimple === 'line') {
      // Find the nearest point on the line
      // Disabled for now as it only seems to find the closest vertex
      //
      // try {
      //   const olFormatGeoJSON = new OLFormatGeoJSON()
      //   // const lineString = olFormatGeoJSON.writeFeatureObject(olFeature)
      //   const lineString = olFormatGeoJSON.writeGeometryObject(olGeometry) as LineString

      //   const nearestPoint = nearestPointOnLine(lineString, coordinate)

      //   console.debug('nearestPoint', nearestPoint)

      //   if (nearestPoint?.geometry?.coordinates) {
      //     whatsHereCoordinates = nearestPoint.geometry.coordinates
      //   }
      // } catch (error) {
      //   console.log('Could not find nearest point on line', error, coordinate, olGeometry)
      // }

      // Below is an OL implementation of nearestPointOnLine, called closestPoint but
      // it does not always result in a point on the linestring, potentially due to rounding errors.
      // See: https://github.com/openlayers/openlayers/issues/7130#issuecomment-535856422
      //
      // const closestPoint = olGeometry.getClosestPoint(coordinate, coordinate)
      // console.debug('getMenuItems - closestPoint', closestPoint, coordinate, olGeometry.intersectsCoordinate(closestPoint))
      // whatsHereCoordinates = closestPoint

      console.debug('getMenuItems - nearest point on line is not implemented yet.')
    }

    const onClickWhatsHere = () => {
      runWhatsHere(whatsHereCoordinates, true)
      dispatchClearMapContextMenu()
    }

    const menuItems = [
      <WhatsHereMenuItem key='whatsHere' menuItemType='whatsHere' onClick={onClickWhatsHere} />
    ]

    // No need to create more menu items if there is no geometry
    if (geomTypeSimple === 'nogeometry') {
      return menuItems
    }

    // Add a divider below the What's Here menu
    menuItems.push(<Divider className={classes.divider} key='whatsHereDivider' />)

    const onClickWhatsThis = () => {
      runWhatsThis(featureInfo)
      dispatchClearMapContextMenu()
    }

    menuItems.push(<WhatsHereMenuItem key='whatsThis' menuItemType='whatsThis' onClick={onClickWhatsThis} />)

    const currentTarget = PoziUtils.convertOLFeatureArrToTurfFeature(
      [featureInfo.olFeature],
      { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' }
    )

    const onClickWhatsIntersectsThis = () => {
      ShowWhatsHere(currentTarget)
      dispatchClearMapContextMenu()
    }

    // Add a What's along this menu item
    if (geomTypeSimple === 'line') {
      menuItems.push(<WhatsHereMenuItem key='whatsAlongThis' menuItemType='whatsAlongThis' onClick={onClickWhatsIntersectsThis} />)
    }

    // Add a What's within this menu item
    if (geomTypeSimple === 'polygon') {
      menuItems.push(<WhatsHereMenuItem key='whatsWithinThis' menuItemType='whatsWithinThis' onClick={onClickWhatsIntersectsThis} />)
    }

    menuItems.push(<WhatsAroundMenuItem closeMenu={dispatchClearMapContextMenu} currentTarget={currentTarget} key='WhatsAroundMenuItem' />)

    // console.debug('getMenuItems - menuItems', menuItems)

    return menuItems
  }

  render = () => {
    const { classes, contextMenu, dispatchClearMapContextMenu } = this.props

    if (!contextMenu) {
      console.log('MapContextMenu - no context menu')

      return null
    }

    const { position } = contextMenu

    const showMenu = !!position

    const menuItems = showMenu ? this.getMenuItems() : null

    // console.debug('MapContextMenu - render - menuItems', menuItems)

    const menuList = (
      <MenuList className={classes.menuList}>
        {menuItems}
      </MenuList>
    )

    return (
      <div>
        <ContextMenu
          onClose={dispatchClearMapContextMenu}
          position={position}
          show={showMenu}
        >
          {menuList}
        </ContextMenu>
      </div>
    )
  }
}

const mapDispatchToProps = (dispatch: any) => {
  return {
    dispatchClearMapContextMenu: () => {
      dispatch(clearMapContextMenu())
    }
  }
}

function mapStateToProps (state: any, ownProps: any) {
  const { mapContextMenu } = state

  const props = {
    contextMenu: mapContextMenu
  }

  return props
}

const MapContextMenu = connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(MapContextMenu_))

export default MapContextMenu
